import React, {useMemo} from "react";
import Header from "../../components/Header";
import "./style.scss";
import {observer} from "mobx-react";
import orderStore from "../../stores/order";
import {getCombinationArticleOptionsUrl, getCurrency, getHost, hasArticleOptions} from "../../helpers/ApiHelper";
import defaultImg from "../../assets/images/default.webp";
import {
    getAddonsFromOrderLine,
    getIngredientsFromOrderLine,
    getModifiersFromOrderLine
} from "../../helpers/orderHelper";
import {useTranslation} from "react-i18next";
import FooterForInfoCombination from "../../components/FooterForInfoCombination";
import {useNavigate} from "react-router";
import OrderIdle from "../../components/OrderIdle";
import IconNotSelected from "../../assets/images/notSelected.png";
import {ReactComponent as IconWarning} from "../../assets/icons/warning.svg";
import useLayoutStyles from "../../hooks/styles";
import menuStore from "../../stores/menu";

const CombinationInfoPage = () => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const styles = useLayoutStyles();

    const combination = useMemo(() => {
        const combinationId = orderStore.currentCombinationOrderLine?.combinationId;
        return combinationId ? menuStore.getCombinationById(combinationId) : undefined;
    }, [orderStore.currentCombinationOrderLine?.combinationId]);

    const getPackageArticle = (stepId: number, articleId: number) => {
        if (!combination?.isPackage) return;
        const step = combination.combination.find(x => x.id === stepId);
        return step?.articles.find(x => x.article.id === articleId)?.article;
    }

    // in a package the article itself can not be changed -> only its modifiers / addons / ingredients
    const allowChange = (stepId: number, articleId: number): boolean => {
        if (!combination?.isPackage) return true;
        const article = getPackageArticle(stepId, articleId);
        return !!article && hasArticleOptions(article);
    }

    const changeStepArticle = (stepId: number, articleId: number) => {
        const line = orderStore.currentCombinationOrderLine;
        if (!line) return;

        if (combination?.isPackage) {
            const step = combination.combination.find(x => x.id === stepId);
            const article = getPackageArticle(stepId, articleId);
            if (!step || !article) return;

            const url = getCombinationArticleOptionsUrl(line.menuId || -1, line.subMenuId || -1, combination, step, article, true);
            if (url)
                navigate(url);
            return;
        }

        navigate(`/menu/combination/${line.menuId}/${line.subMenuId}/${line.combinationId}/step/${stepId}/change`)
    }

    if (!orderStore.currentCombinationOrderLine) return null;

    return <div className="CombinationInfoPage">
        <Header/>
        <div className="content">
            <h2>{orderStore.currentCombinationOrderLine.name}</h2>
            <div className="totalPrice">{(orderStore.currentCombinationOrderLine?.total || 0).toFixed(2)} {getCurrency()}</div>
            <div className="orderLines">
                {orderStore.currentCombinationOrderLine.steps.map(step => <div key={`step_${step.id}`} className="step">
                    <div className="stepTitle">{step.name}</div>
                    {step.articles.map(x => <div key={`orderline_${x.id}`} className="orderLine">
                        <div className="img">
                            {x.articleId !== -1
                                ? <img alt={x.name} src={x.img ? `${getHost()}${x.img}` : (styles?.article.defaultListImage ? `${getHost()}${styles.article.defaultListImage}` : defaultImg)}/>
                                : <img alt="not selected" src={IconNotSelected} />
                            }
                        </div>
                        <div className="text">
                            <div className="title">
                                {x.articleId !== -1
                                    ? <>{x.count} x {x.name}</>
                                    : <>
                                        <div className="notSelected">{t("notSelected")}</div>
                                        <div className="noChoiceMade">
                                            <IconWarning />
                                            {t("noChoiceMade")}
                                        </div>
                                    </>
                                }
                            </div>
                            <div className="details" key={x.id}>
                                {getModifiersFromOrderLine(x).map(m => m.name).concat(getAddonsFromOrderLine(x).map(a => a.name), getIngredientsFromOrderLine(x).map(a => a.name)).join(", ")}
                            </div>
                        </div>
                        <div className="buttons">
                            {allowChange(step.id, x.articleId || -1)
                                ? <div className="button" onClick={() => changeStepArticle(step.id, x.articleId || -1)}>{t("change")}</div>
                                : null
                            }
                        </div>
                    </div>)}
                </div>)}
            </div>
        </div>
        <FooterForInfoCombination />
        <OrderIdle />
    </div>
}

export default observer(CombinationInfoPage);
